import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

export interface AdminListResult<T> {
  rows: T[];
  total: number;
  hasMore: boolean;
}

export type SortDirection = 'asc' | 'desc';

interface UseAdminListOptions<T> {
  /** Stable cache-key segment for this list, e.g. 'users' */
  queryKey: string;
  /** API endpoint relative to the api client baseURL, e.g. '/admin/users' */
  endpoint: string;
  /**
   * Maps the endpoint's raw response to { rows, total, hasMore }. Must be a
   * stable function (defined at module scope, not inline per render).
   */
  parse: (raw: unknown) => AdminListResult<T>;
  /** Extra query params (filters). Changing them resets to page 1. Array
   * values are sent as repeated params (multi-select filters). */
  filters?: Record<string, string | number | boolean | string[] | undefined>;
  limit?: number;
  debounceMs?: number;
  /** Server-side sort applied on first render (matches the endpoint default). */
  initialSort?: { key: string; direction: SortDirection };
  /** Param names the endpoint expects for sorting (default: sort / order). */
  sortParamNames?: { sort: string; order: string };
}

/**
 * The standard list-fetching hook for the new admin suite: React Query cache,
 * debounced server-side search, and offset pagination — one implementation
 * instead of one per page.
 */
export function useAdminList<T>({
  queryKey,
  endpoint,
  parse,
  filters,
  limit = 25,
  debounceMs = 300,
  initialSort,
  sortParamNames = { sort: 'sort', order: 'order' },
}: UseAdminListOptions<T>) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [sortKey, setSortKey] = useState<string>(initialSort?.key ?? '');
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSort?.direction ?? 'asc');

  /** Column-header click: same key toggles direction, new key sorts ascending. */
  const handleSort = (key: string) => {
    setOffset(0);
    if (key === sortKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setOffset(0);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [search, debounceMs]);

  const filtersKey = JSON.stringify(filters ?? {});
  useEffect(() => {
    setOffset(0);
  }, [filtersKey]);

  const query = useQuery({
    queryKey: ['admin-list', queryKey, debouncedSearch, offset, limit, filtersKey, sortKey, sortDirection],
    queryFn: async () => {
      const response = await api.get(endpoint, {
        params: {
          search: debouncedSearch || undefined,
          limit,
          offset,
          ...(sortKey
            ? { [sortParamNames.sort]: sortKey, [sortParamNames.order]: sortDirection }
            : {}),
          ...filters,
        },
      });
      return parse(response.data);
    },
    keepPreviousData: true,
  });

  const result = query.data;
  const total = result?.total ?? 0;

  return {
    // search
    search,
    setSearch,
    // sorting
    sortKey,
    sortDirection,
    handleSort,
    // data
    rows: result?.rows ?? [],
    total,
    // status
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    // pagination
    limit,
    offset,
    page: Math.floor(offset / limit) + 1,
    pageCount: Math.max(1, Math.ceil(total / limit)),
    hasPrev: offset > 0,
    hasNext: result?.hasMore ?? false,
    nextPage: () => setOffset((current) => current + limit),
    prevPage: () => setOffset((current) => Math.max(0, current - limit)),
  };
}
